import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { sha256 } from "js-sha256";
import styles from "./AdminShared.module.css";
import AdminFieldForm from "./AdminFieldForm";
import { useAdminUI } from "./AdminUIContext";
import {
  itemsToArray,
  getRequiredFieldsForItem,
  getResponseStatus,
  getResponseErrorMessage,
  toTitleCase,
  formatShortIdentifier,
  DEFAULT_CATALOG_TYPES,
  DEFAULT_CONTENT_TYPES,
  getUniqueTypes,
  getItemTypeThemeKey,
  isTruthyFlag,
} from "./adminUtils";
import {
  formatReleaseFullDate,
  isUpcomingRelease,
} from "../../../Hooks/catalogPresentation";
import {
  updateACatalogItem,
  patchACatalogItem,
  deleteDocFromDb,
} from "../../../storage/catalogDB";
import {
  updateAContentItem,
  patchAContentItem,
  deleteContentDocFromDb,
} from "../../../storage/contentDB";
import { catalogDataActions } from "../../../store/catalogDataSlice";
import { contentDataActions } from "../../../store/contentDataSlice";
import {
  packConnectSplashIntoItem,
  isConnectSplashType,
} from "../../../data/connectSplashConfig";
import {
  packJoinInviteIntoItem,
  isJoinInviteType,
} from "../../../data/joinInviteConfig";
import {
  formValuesFromItem,
  serializeDraftForSave,
  schemaSupportsDraft,
  canSaveDraftForType,
  detectDraftFieldStripped,
  itemHasDraft,
  isNeverPublished,
  canRevertToPublished,
  publishConfirmCopy,
  publishedValuesFromItem,
} from "../../../data/cmsDraft";
import {
  buildPreviewItemFromForm,
  openAdminPreview,
  previewItemLabel,
  removePreviewItem,
  upsertPreviewItem,
} from "../../../data/cmsPreview";
import {
  useIsInPreviewSet,
  usePreviewStores,
} from "../../../data/CmsPreviewContext";
import AdminPreviewActions from "./AdminPreviewActions";

const AdminItemEditor = ({ section = "catalog" }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast, confirm } = useAdminUI();
  const user = useSelector((state) => state.auth.user);
  const catalog = useSelector((state) => state.catalogData.catalog);
  const content = useSelector((state) => state.contentData.content);
  const catalogModel = useSelector((state) => state.catalogData.catalogModel);
  const contentModel = useSelector((state) => state.contentData.contentModel);
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );

  const model = section === "content" ? contentModel : catalogModel;
  const store = section === "content" ? content : catalog;

  const services = useMemo(() => {
    const avail = catalogMetadata?.availableServices || [];
    const inactive = catalogMetadata?.inactiveServices || [];
    return [...new Set([...avail, ...inactive].filter(Boolean))];
  }, [catalogMetadata]);

  const sourceItem = useMemo(() => {
    const items = itemsToArray(store);
    const decoded = decodeURIComponent(id || "");
    return (
      items.find(
        (i) =>
          String(i._id) === decoded ||
          String(i.identifier) === decoded ||
          String(i._storeKey) === decoded,
      ) || null
    );
  }, [store, id]);

  // Stable fingerprint so re-fetch / save merges re-hydrate without thrashing on object identity
  const sourceFingerprint = useMemo(() => {
    if (!sourceItem) return "";
    return [
      sourceItem._id,
      sourceItem.identifier,
      sourceItem.updatedAt,
      // Explicitly include pre-release copy — this was the field users could not see after save
      sourceItem.preReleaseMessage == null
        ? ""
        : String(sourceItem.preReleaseMessage),
      sourceItem.text == null ? "" : String(sourceItem.text),
      sourceItem.title,
      sourceItem.releaseDate,
      sourceItem.draftPayload == null ? "" : String(sourceItem.draftPayload),
      sourceItem.publishedAt == null ? "" : String(sourceItem.publishedAt),
    ]
      .map((v) => (v == null ? "" : String(v)))
      .join("|");
  }, [sourceItem]);

  const [values, setValues] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSystem, setShowSystem] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftsBlocked, setDraftsBlocked] = useState(false);
  const { previewItems } = usePreviewStores();
  const previewIdentifier =
    (values && (values.identifier || values._localId)) ||
    sourceItem?.identifier ||
    "";
  const includedInPreview = useIsInPreviewSet(section, previewIdentifier);

  // Switching items always resets the form
  useEffect(() => {
    setDirty(false);
    setErrors({});
  }, [id]);

  useEffect(() => {
    if (!sourceItem) {
      setValues(null);
      return;
    }
    // Re-seed from store when the saved record changes. Never clobber in-progress edits.
    // sourceFingerprint tracks the item fields we care about (incl. preReleaseMessage).
    if (dirty) return;
    setValues(formValuesFromItem(sourceItem, services));
    setErrors({});
  }, [sourceFingerprint, services, dirty, id, sourceItem]);

  // Browser tab close / refresh — native dialog (browsers ignore custom copy)
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue =
        "You have unsaved changes. Leave this page and discard them?";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  const onChange = useCallback((field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  /** In-app leave with confirm when there are unsaved edits */
  const confirmLeaveIfDirty = useCallback(async () => {
    if (!dirty) return true;
    return confirm({
      title: "Leave without saving?",
      message:
        "You have unsaved changes. If you leave now, those edits will be lost.",
      confirmLabel: "Leave without saving",
      danger: true,
    });
  }, [dirty, confirm]);

  const typeOptions = useMemo(() => {
    const existing = getUniqueTypes(itemsToArray(store));
    const defaults =
      section === "content" ? DEFAULT_CONTENT_TYPES : DEFAULT_CATALOG_TYPES;
    return Array.from(new Set([...defaults, ...existing]));
  }, [store, section]);

  const validate = () => {
    const required = getRequiredFieldsForItem(
      model,
      values?.type,
      section,
    );
    const nextErrors = {};
    required.forEach((field) => {
      const v = values?.[field];
      if (v == null || String(v).replace(/\s/g, "") === "") {
        nextErrors[field] = "Required";
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const draftsSupported = schemaSupportsDraft(model) && !draftsBlocked;
  const typeAllowsDraft = canSaveDraftForType(values?.type);
  const canSaveDraft = draftsSupported && typeAllowsDraft;

  const upsertSaved = (savedDoc) => {
    if (!savedDoc) return;
    if (section === "catalog") {
      dispatch(catalogDataActions.upsertItem(savedDoc));
    } else {
      dispatch(contentDataActions.upsertItem(savedDoc));
    }
  };

  const handleSaveDraft = async () => {
    if (!values) return;
    if (!validate()) {
      toast.error("Please fill all required fields.");
      return;
    }
    if (!canSaveDraft) {
      toast.error(
        draftsSupported
          ? "Save draft is not available for this type yet. Use Publish to go live."
          : "Drafts need a backend deploy (draftPayload). Use Publish to go live.",
      );
      return;
    }

    const identifier = values.identifier;
    if (!identifier) {
      toast.error("Missing identifier — cannot update this item.");
      return;
    }

    setSaving(true);
    try {
      const envelope = serializeDraftForSave(values, {
        section,
        type: values.type,
      });
      const patchFn =
        section === "content" ? patchAContentItem : patchACatalogItem;
      const res = await patchFn(identifier, envelope, user);
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        const savedDoc = res?.data?.doc;
        if (detectDraftFieldStripped(envelope.draftPayload, savedDoc)) {
          setDraftsBlocked(true);
          toast.error(
            "Drafts need a backend deploy (draftPayload). Use Publish to go live.",
          );
          return;
        }
        toast.success("Draft saved.");
        setDirty(false);
        upsertSaved(savedDoc);
        if (savedDoc) setValues(formValuesFromItem(savedDoc, services));
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Save draft failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!values) return;
    if (!validate()) {
      toast.error("Please fill all required fields.");
      return;
    }

    const identifier = values.identifier;
    if (!identifier) {
      toast.error("Missing identifier — cannot update this item.");
      return;
    }

    const otherActiveSameType =
      (isJoinInviteType(values.type) || isConnectSplashType(values.type)) &&
      isTruthyFlag(values.active) &&
      itemsToArray(store).some(
        (i) =>
          String(i.type || "")
            .trim()
            .toLowerCase() ===
            String(values.type || "")
              .trim()
              .toLowerCase() &&
          isTruthyFlag(i.active) &&
          String(i.identifier) !== String(values.identifier),
      );

    const ok = await confirm(
      publishConfirmCopy({
        visitorsWillSeeIt: isTruthyFlag(values.active),
        otherActiveSameType,
        mode: "edit",
      }),
    );
    if (!ok) return;

    setSaving(true);
    try {
      const payload = { ...values };
      delete payload._storeKey;
      delete payload._id;
      delete payload.__v;

      if (payload.sourceURLObj && typeof payload.sourceURLObj === "object") {
        const cleaned = { ...payload.sourceURLObj };
        Object.keys(cleaned).forEach((k) => {
          if (!cleaned[k]) cleaned[k] = "";
        });
        payload.sourceURLObj = cleaned;
      }

      if (section === "catalog") {
        payload.preReleaseMessage =
          values.preReleaseMessage == null
            ? ""
            : String(values.preReleaseMessage);
        payload.defaultEmbedQuery =
          values.defaultEmbedQuery == null
            ? ""
            : String(values.defaultEmbedQuery);
      }

      payload.updatedAt = new Date();
      payload.draftPayload = "";
      payload.publishedAt = new Date();

      const updateFn =
        section === "content" ? updateAContentItem : updateACatalogItem;
      const res = await updateFn(
        identifier,
        packJoinInviteIntoItem(packConnectSplashIntoItem(payload)),
        user,
      );
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        toast.success("Published.");
        setDirty(false);
        removePreviewItem({ section, identifier });
        const savedDoc = res?.data?.doc;
        upsertSaved(savedDoc);
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
        navigate(`/manage/${section}`);
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Publish failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete this item?",
      message: `Permanently delete "${
        values?.title || values?.slug || id
      }"? This cannot be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;

    const identifier = values?.identifier;
    if (!identifier) {
      toast.error("Missing identifier.");
      return;
    }

    setSaving(true);
    try {
      const deleteFn =
        section === "content" ? deleteContentDocFromDb : deleteDocFromDb;
      const res = await deleteFn(identifier, user);
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        toast.success("Item deleted.");
        setDirty(false);
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
        navigate(`/manage/${section}`);
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (splash) => {
    if (!values) return;
    if (!validate()) {
      toast.error("Please fill all required fields.");
      return;
    }
    const result = openAdminPreview({
      values,
      section,
      dirty,
      sourceItem,
      splash,
    });
    if (result.replaced) {
      toast.info(
        `Replaced the other ${previewItemLabel(result.replaced)} in the preview set.`,
      );
    }
    if (!result.ok) {
      toast.error(
        "Could not store unsaved preview. Opening a saved-draft URL if one exists.",
      );
    }
  };

  const handleToggleInclude = () => {
    if (!values) return;
    if (!validate()) {
      toast.error("Please fill all required fields.");
      return;
    }
    const identifier = values.identifier || values._localId || "";
    if (includedInPreview) {
      removePreviewItem({ section, identifier });
      toast.success("Removed from the preview set.");
      return;
    }
    const result = upsertPreviewItem(
      buildPreviewItemFromForm({ values, section, dirty, sourceItem }),
    );
    if (!result.ok) {
      toast.error("Could not add this item to the preview set.");
      return;
    }
    if (result.replaced) {
      toast.info(
        `Replaced the other ${previewItemLabel(result.replaced)} in the preview set.`,
      );
    } else {
      toast.success(
        result.itemCount > 1
          ? `Added to the preview set (${result.itemCount} items).`
          : "Added to the preview set.",
      );
    }
  };

  const handleRevertToPublished = async () => {
    if (!canRevertToPublished(sourceItem, { dirty })) {
      toast.error(
        "This item has never been published, so there is no published copy to restore. Delete the item to throw it away.",
      );
      return;
    }
    const hasSavedDraft = itemHasDraft(sourceItem);
    const ok = await confirm({
      title: "Revert to published copy?",
      message: hasSavedDraft
        ? "This removes the unpublished draft and restores the last published copy in this form. Visitors are not affected."
        : "This throws away unsaved edits and restores the last published copy in this form. Visitors are not affected.",
      confirmLabel: "Revert to published",
      danger: true,
    });
    if (!ok) return;

    if (!hasSavedDraft) {
      setValues(publishedValuesFromItem(sourceItem, services));
      setDirty(false);
      toast.success("Restored the published copy.");
      return;
    }

    const identifier = values?.identifier;
    if (!identifier) {
      toast.error("Missing identifier.");
      return;
    }

    setSaving(true);
    try {
      const patchFn =
        section === "content" ? patchAContentItem : patchACatalogItem;
      const res = await patchFn(identifier, { draftPayload: "" }, user);
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        toast.success("Restored the published copy.");
        setDirty(false);
        const savedDoc = res?.data?.doc;
        if (savedDoc) {
          upsertSaved(savedDoc);
          setValues(publishedValuesFromItem(savedDoc, services));
        } else {
          setValues(publishedValuesFromItem(sourceItem, services));
        }
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Revert failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!values) return;
    const ok = await confirm({
      title: "Duplicate item?",
      message:
        "Open a new draft based on this item. You will still need to save it.",
      confirmLabel: "Continue",
    });
    if (!ok) return;

    const draft = { ...values };
    delete draft._id;
    delete draft._storeKey;
    delete draft.identifier;
    draft.title = draft.title ? `${draft.title} (copy)` : "Copy";
    if (draft.slug) draft.slug = `${draft.slug}-copy`;
    draft.active = "false";
    const date = new Date();
    draft.createdAt = date;
    draft.updatedAt = date;
    const hashID = sha256(JSON.stringify(draft.title + Date.now()));
    draft.identifier = date.getFullYear() + "-" + hashID;

    try {
      sessionStorage.setItem(
        "ms1-admin-create-draft",
        JSON.stringify({ section, draft }),
      );
      navigate(`/manage/${section}/new?from=duplicate`);
    } catch {
      toast.error("Could not prepare duplicate draft.");
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (dirty && !saving) handleSaveDraft();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dirty, saving, values]);

  const listPath = `/manage/${section}`;
  const fullIdentifier = values?.identifier || values?._id || "";
  const shortIdentifier = formatShortIdentifier(fullIdentifier);

  const copyIdentifier = async () => {
    if (!fullIdentifier) return;
    try {
      await navigator.clipboard.writeText(String(fullIdentifier));
      toast.success("Identifier copied.");
    } catch {
      toast.error("Could not copy identifier.");
    }
  };

  if (!store) {
    return (
      <div className={styles["loading-block"]}>Loading item data…</div>
    );
  }

  if (!sourceItem || !values) {
    return (
      <div className={styles.panel}>
        <div className={styles["empty-state"]}>
          <h3>Item not found</h3>
          <p>No {section} item matches this ID.</p>
          <Link to={listPath} className={styles["btn-primary"]}>
            Back to list
          </Link>
        </div>
      </div>
    );
  }

  const typeKey = getItemTypeThemeKey(values.type);
  const typeSurfaceClass = typeKey
    ? `${styles["item-type-surface"]} ${styles[`item-type-${typeKey}`]}`
    : "";
  const isUpcoming = isUpcomingRelease(values);
  const upcomingDropLabel = isUpcoming ? formatReleaseFullDate(values) : "";

  const saveButtonClass = dirty
    ? `${styles["btn-primary"]} ${styles["btn-save-needed"]}`
    : styles["btn-primary"];

  const handleNavigateAway = async () => {
    if (!(await confirmLeaveIfDirty())) return;
    navigate(listPath);
  };

  return (
    <div className={dirty ? styles["page-has-unsaved"] : undefined}>
      <div className={`${styles["page-header"]} ${styles["page-header-stacked"]}`}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>
            {dirty && (
              <span
                className={styles["dirty-dot"]}
                title="Unsaved changes"
                aria-hidden="true"
              />
            )}
            {values.title || "Edit item"}
          </h1>
          <div className={styles["page-subtitle"]}>
            <span className={styles["subtitle-meta"]}>
              <span>{toTitleCase(section)}</span>
              <span className={styles["subtitle-sep"]} aria-hidden="true">
                ·
              </span>
              {typeKey ? (
                <span
                  className={`${styles["badge-item-type"]} ${styles[`item-type-${typeKey}`]}`}
                >
                  {toTitleCase(typeKey)}
                </span>
              ) : (
                <span>
                  {values.type ? toTitleCase(values.type) : "No type"}
                </span>
              )}
              {isUpcoming && (
                <span
                  className={`${styles.badge} ${styles["badge-upcoming"]}`}
                  title={
                    upcomingDropLabel
                      ? `Drops ${upcomingDropLabel}`
                      : "Upcoming release"
                  }
                >
                  Upcoming
                  {upcomingDropLabel ? ` · ${upcomingDropLabel}` : ""}
                </span>
              )}
              {itemHasDraft(sourceItem) && (
                <span className={styles.badge} title="Unpublished draft saved">
                  Draft
                </span>
              )}
              {isNeverPublished(sourceItem) && (
                <span className={styles.badge} title="Not on the live site yet">
                  Never published
                </span>
              )}
              {dirty && (
                <>
                  <span className={styles["subtitle-sep"]} aria-hidden="true">
                    ·
                  </span>
                  <span className={styles["unsaved-inline-label"]}>
                    Unsaved changes
                  </span>
                </>
              )}
            </span>
            {fullIdentifier ? (
              <span className={styles["id-row"]}>
                <code
                  className={styles["id-chip"]}
                  title={String(fullIdentifier)}
                >
                  {shortIdentifier}
                </code>
                <button
                  type="button"
                  className={styles["id-copy-btn"]}
                  onClick={copyIdentifier}
                  title="Copy full identifier"
                  aria-label="Copy full identifier"
                >
                  Copy ID
                </button>
              </span>
            ) : null}
          </div>
        </div>
        <div className={styles["page-toolbar"]}>
          <div className={styles["action-group"]} role="group" aria-label="Navigate">
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={handleNavigateAway}
            >
              Back
            </button>
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={handleDuplicate}
              disabled={saving}
            >
              Duplicate
            </button>
          </div>
          <AdminPreviewActions
            type={values.type}
            onPreview={handlePreview}
            disabled={saving}
            included={includedInPreview}
            onToggleInclude={handleToggleInclude}
            includeCount={previewItems.length}
          />
          <div className={styles["action-group"]} role="group" aria-label="Save">
            {canRevertToPublished(sourceItem, { dirty }) && (
              <button
                type="button"
                className={styles["btn-ghost"]}
                onClick={handleRevertToPublished}
                disabled={saving}
                title="Restore the last published copy in this form"
              >
                Revert
              </button>
            )}
            <button
              type="button"
              className={saveButtonClass}
              onClick={handleSaveDraft}
              disabled={saving || !dirty || !canSaveDraft}
              aria-live="polite"
              title={
                canSaveDraft
                  ? "Save without changing the live site (⌘S)"
                  : draftsSupported
                    ? "Save draft is not available for this type yet"
                    : "Drafts need a backend deploy"
              }
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={handlePublish}
              disabled={saving}
            >
              Publish
            </button>
          </div>
        </div>
      </div>

      {!draftsSupported && (
        <p className={styles["field-hint"]} role="status">
          Drafts need a backend deploy (<code>draftPayload</code>). Use Publish
          to go live.
        </p>
      )}
      {draftsSupported && !typeAllowsDraft && (
        <p className={styles["field-hint"]} role="status">
          Save draft is not available for this type yet. Use Publish to go live.
        </p>
      )}

      <div className={`${styles.panel} ${typeSurfaceClass}`.trim()}>
        <AdminFieldForm
          model={model}
          values={values}
          onChange={onChange}
          errors={errors}
          services={services}
          section={section}
          showSystem={showSystem}
          onToggleSystem={() => setShowSystem((s) => !s)}
          mode="edit"
          typeOptions={typeOptions}
        />

        <div className={styles["form-footer"]}>
          <button
            type="button"
            className={styles["btn-danger"]}
            onClick={handleDelete}
            disabled={saving}
          >
            Delete item
          </button>
          <div
            className={`${styles["form-footer-actions"]} ${styles["action-group"]}`}
            role="group"
            aria-label="Save"
          >
            <button
              type="button"
              className={saveButtonClass}
              onClick={handleSaveDraft}
              disabled={saving || !dirty || !canSaveDraft}
            >
              {saving ? "Saving…" : "Save draft"}
            </button>
            <button
              type="button"
              className={styles["btn-secondary"]}
              onClick={handlePublish}
              disabled={saving}
            >
              Publish
            </button>
          </div>
        </div>
      </div>

      {dirty && (
        <div
          className={styles["unsaved-bar"]}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div className={styles["unsaved-bar-inner"]}>
            <p className={styles["unsaved-bar-text"]}>
              <span className={styles["unsaved-bar-dot"]} aria-hidden="true" />
              You have unsaved changes
            </p>
            <div className={styles["unsaved-bar-actions"]}>
              <div className={styles["action-group"]} role="group" aria-label="Unsaved changes">
                <button
                  type="button"
                  className={styles["btn-ghost"]}
                  onClick={handleNavigateAway}
                  disabled={saving}
                  title="Leave without saving"
                >
                  Leave
                </button>
                {canRevertToPublished(sourceItem, { dirty }) && (
                  <button
                    type="button"
                    className={styles["btn-ghost"]}
                    onClick={handleRevertToPublished}
                    disabled={saving}
                    title="Restore the last published copy in this form"
                  >
                    Revert
                  </button>
                )}
                <button
                  type="button"
                  className={saveButtonClass}
                  onClick={handleSaveDraft}
                  disabled={saving || !canSaveDraft}
                >
                  {saving ? "Saving…" : "Save draft"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminItemEditor;
